import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardBody, CardHeader, Row, Col, Spinner, Alert, Button } from 'reactstrap';
import { 
 getFunctionsOrganization, 
  getUserInThisFunction 
} from '../../services/functions';
import { 
  FaUsers, 
  FaRedo,
  FaExpand,
  FaCompress,
  FaChevronDown,
  FaChevronRight,
  FaUser,
  FaUserTie,
  FaUserCog,
  FaCrown,
  FaFilePdf,
  FaDownload,
  FaGripVertical
} from 'react-icons/fa';
// PDF export will be implemented using browser's print functionality
import './OrganizationChart.css';

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  code: string;
}

interface Function {
  id: string;
  name: string;
  description?: string;
}

interface FunctionWithUsers extends Function {
  users: User[];
  userCount: number;
  isExpanded?: boolean;
  level?: number;
  parentId?: string;
}

interface TreeNode {
  id: string;
  name: string;
  type: 'ceo' | 'vp' | 'director' | 'user';
  level: number;
  parentId?: string;
  children: TreeNode[];
  user?: User;
  function?: FunctionWithUsers;
  isExpanded: boolean;
  role?: string;
}

const COLORS = ['#00C49F', '#F44336', '#FF9800', '#9E9E9E', '#2196F3', '#4CAF50', '#FF5722', '#9C27B0', '#607D8B', '#795548'];

const OrganizationChart: React.FC = () => {
  const [functions, setFunctions] = useState<FunctionWithUsers[]>([]);
  const [treeData, setTreeData] = useState<TreeNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'tree' | 'list'>('tree');
  const [expandedView, setExpandedView] = useState(false);
  const [selectedFunction, setSelectedFunction] = useState<FunctionWithUsers | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [draggedUser, setDraggedUser] = useState<User | null>(null);
  const [dragOverNode, setDragOverNode] = useState<string | null>(null);
  const chartRef = useRef<HTMLDivElement>(null);

  const buildTreeStructure = (functionsData: FunctionWithUsers[]): TreeNode[] => {
    // Create CEO node
    const ceoNode: TreeNode = {
      id: 'ceo',
      name: 'CEO',
      type: 'ceo',
      level: 0,
      children: [],
      isExpanded: true,
      role: 'CEO'
    };

    // Group functions by role to create VP and Director levels
    const vpFunctions = functionsData.slice(0, 4); // First 4 functions as VPs
    const directorFunctions = functionsData.slice(4); // Rest as Directors

    // Create VP nodes
    vpFunctions.forEach((func, index) => {
      const vpNode: TreeNode = {
        id: `vp-${func.id}`,
        name: `VP ${index + 1}`,
        type: 'vp',
        level: 1,
        parentId: 'ceo',
        children: [],
        isExpanded: false,
        function: func,
        role: 'VP'
      };

      // Add users as directors under VP
      func.users.forEach((user, userIndex) => {
        const directorNode: TreeNode = {
          id: `director-${user.id}`,
          name: `Director ${userIndex + 1}`,
          type: 'director',
          level: 2,
          parentId: `vp-${func.id}`,
          children: [],
          isExpanded: false,
          user: user,
          role: 'Director'
        };
        vpNode.children.push(directorNode);
      });

      ceoNode.children.push(vpNode);
    });

    // Add remaining functions as additional VPs if needed
    if (functionsData.length > 4) {
      const remainingFunctions = functionsData.slice(4);
      remainingFunctions.forEach((func, index) => {
        const vpNode: TreeNode = {
          id: `vp-${func.id}`,
          name: `VP ${vpFunctions.length + index + 1}`,
          type: 'vp',
          level: 1,
          parentId: 'ceo',
          children: [],
          isExpanded: false,
          function: func,
          role: 'VP'
        };

        // Add users as directors
        func.users.forEach((user, userIndex) => {
          const directorNode: TreeNode = {
            id: `director-${user.id}`,
            name: `Director ${userIndex + 1}`,
            type: 'director',
            level: 2,
            parentId: `vp-${func.id}`,
            children: [],
            isExpanded: false,
            user: user,
            role: 'Director'
          };
          vpNode.children.push(directorNode);
        });

        ceoNode.children.push(vpNode);
      });
    }

    return [ceoNode];
  };

  const toggleNode = (nodeId: string) => {
    const updateNode = (nodes: TreeNode[]): TreeNode[] => {
      return nodes.map(node => {
        if (node.id === nodeId) {
          return { ...node, isExpanded: !node.isExpanded };
        }
        if (node.children.length > 0) {
          return { ...node, children: updateNode(node.children) };
        }
        return node;
      });
    };
    setTreeData(updateNode(treeData));
  };

  const handleDragStart = (e: React.DragEvent, user: User) => {
    setDraggedUser(user);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', user.id);
  };

  const handleDragOver = (e: React.DragEvent, nodeId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverNode(nodeId);
  };

  const handleDragLeave = () => {
    setDragOverNode(null);
  };

  const handleDrop = (e: React.DragEvent, targetNodeId: string) => {
    e.preventDefault();
    
    if (!draggedUser) return;

    // Find the target node and move the user
    const moveUserToNode = (nodes: TreeNode[]): TreeNode[] => {
      return nodes.map(node => {
        if (node.id === targetNodeId) {
          // Add user to target node
          const updatedUser = { ...draggedUser, role: node.type };
          const updatedChildren = [...node.children, {
            id: `user-${draggedUser.id}`,
            name: draggedUser.name,
            type: 'user' as const,
            level: node.level + 1,
            parentId: node.id,
            children: [],
            user: updatedUser,
            isExpanded: false
          }];
          
          return { ...node, children: updatedChildren };
        }
        
        // Remove user from current location
        const filteredChildren = node.children.filter(child => 
          !(child.type === 'user' && child.user?.id === draggedUser.id)
        );
        
        if (filteredChildren.length !== node.children.length) {
          return { ...node, children: filteredChildren };
        }
        
        // Recursively search in children
        if (node.children.length > 0) {
          return { ...node, children: moveUserToNode(node.children) };
        }
        
        return node;
      });
    };

    setTreeData(moveUserToNode(treeData));
    setDraggedUser(null);
    setDragOverNode(null);
  };

  const handleDragEnd = () => {
    setDraggedUser(null);
    setDragOverNode(null);
  };

  const getRoleIcon = (nodeType: string, role?: string) => {
    switch (nodeType) {
      case 'ceo':
        return <FaCrown className="role-icon ceo" />;
      case 'vp':
        return <FaUserTie className="role-icon vp" />;
      case 'director':
        return <FaUserCog className="role-icon director" />;
      case 'user':
        return <FaUser className="role-icon user" />;
      default:
        return <FaUser className="role-icon user" />;
    }
  };

  const fetchOrganizationData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch all functions
      const functionsResponse = await getFunctionsOrganization('', 100, 0);
      
      if (!functionsResponse.success) {
        throw new Error('Failed to fetch functions');
      }

      const functionsData: Function[] = functionsResponse.data || [];
      
      // Fetch users for each function
      const functionsWithUsers: FunctionWithUsers[] = await Promise.all(
        functionsData.map(async (func) => {
          try {
            const usersResponse = await getUserInThisFunction(func.id);
            const users: User[] = usersResponse.success ? (usersResponse.data || []).map((user: any) => ({
              id: user.id || '',
              name: user.name || user.username || 'Unknown User',
              email: user.email || '',
              role: user.role || '',
              code: user.code || ''
            })) : [];
            
            return {
              ...func,
              users,
              userCount: users.length
            };
          } catch (err) {
            console.error(`Error fetching users for function ${func.name}:`, err);
            return {
              ...func,
              users: [],
              userCount: 0
            };
          }
        })
      );

      // Sort by user count descending
      functionsWithUsers.sort((a, b) => b.userCount - a.userCount);
      
      setFunctions(functionsWithUsers);
      
      // Build tree structure
      const tree = buildTreeStructure(functionsWithUsers);
      setTreeData(tree);
    } catch (err: any) {
      console.error('Error fetching organization data:', err);
      setError(err.message || 'Failed to load organization data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrganizationData();
  }, [fetchOrganizationData]);

  const exportToPDF = () => {
    try {
      setIsExporting(true);
      
      // Get the chart content
      const chartContent = chartRef.current?.innerHTML || '';
      
      // Create a new window for PDF generation
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        setError('Unable to open print window. Please allow popups for this site.');
        return;
      }
      
      // Create the PDF content with formal styling
      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Organizational Chart - ${new Date().toLocaleDateString()}</title>
          <style>
            @page { 
              size: A4 landscape; 
              margin: 15mm;
            }
            body { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
              margin: 0; 
              padding: 20px; 
              background: white; 
              color: #333;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .print-header { 
              text-align: center; 
              margin-bottom: 40px; 
              border-bottom: 3px solid #2c3e50; 
              padding-bottom: 20px; 
            }
            .print-header h1 { 
              color: #2c3e50; 
              font-size: 28px; 
              font-weight: bold; 
              margin: 0; 
              letter-spacing: 2px; 
            }
            .print-header p { 
              color: #666; 
              font-size: 16px; 
              margin: 10px 0 0 0; 
            }
            .print-header .date { 
              color: #999; 
              font-size: 12px; 
              margin: 5px 0 0 0; 
            }
            .tree-view { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
              display: flex; 
              flex-direction: column; 
              align-items: center; 
              width: 100%; 
            }
            .org-chart-node { 
              position: relative; 
              margin: 8px 0; 
            }
            .org-box { 
              background: transparent; 
              border: 2px solid #e0e0e0; 
              border-radius: 4px; 
              min-width: 200px; 
              position: relative; 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
              page-break-inside: avoid;
            }
            .org-box.ceo { 
              background: transparent; 
              color: #2c3e50; 
              border: 2px solid #2c3e50; 
              min-width: 250px; 
              font-weight: 600;
            }
            .org-box.vp { 
              background: transparent; 
              color: #495057; 
              border: 2px solid #495057; 
              min-width: 220px; 
              font-weight: 500;
            }
            .org-box.director { 
              background: transparent; 
              color: #6c757d; 
              border: 2px solid #6c757d; 
              min-width: 180px; 
              font-weight: 500;
            }
            .org-box.user { 
              background: transparent; 
              color: #868e96; 
              border: 2px solid #868e96; 
              min-width: 160px; 
              font-weight: 400;
            }
            .org-chart-children { 
              display: flex; 
              flex-direction: row; 
              justify-content: center; 
              align-items: flex-start; 
              margin-top: 30px; 
              position: relative; 
              flex-wrap: wrap; 
              gap: 25px; 
            }
            .org-chart-children::before { 
              content: ''; 
              position: absolute; 
              top: -15px; 
              left: 50%; 
              transform: translateX(-50%); 
              width: 2px; 
              height: 15px; 
              background: #ddd; 
            }
            .org-chart-node.vp { 
              display: flex; 
              flex-direction: column; 
              align-items: center; 
              margin: 0; 
              position: relative; 
            }
            .org-chart-children .org-chart-node.vp { 
              display: flex; 
              flex-direction: column; 
              align-items: center; 
              min-width: 180px; 
            }
            .org-chart-node.vp .org-chart-children { 
              display: flex; 
              flex-direction: column; 
              align-items: center; 
              margin-top: 15px; 
              gap: 8px; 
            }
            .org-chart-node.vp .org-chart-children::before { 
              content: ''; 
              position: absolute; 
              top: -8px; 
              left: 50%; 
              transform: translateX(-50%); 
              width: 2px; 
              height: 8px; 
              background: #ddd; 
            }
            .org-chart-node.director { 
              margin: 4px 0; 
              position: relative; 
            }
            .org-box.vp::before { 
              content: ''; 
              position: absolute; 
              top: -15px; 
              left: 50%; 
              transform: translateX(-50%); 
              width: 2px; 
              height: 15px; 
              background: #ddd; 
            }
            .org-box.director::before { 
              content: ''; 
              position: absolute; 
              top: -8px; 
              left: 50%; 
              transform: translateX(-50%); 
              width: 2px; 
              height: 8px; 
              background: #ddd; 
            }
            .org-chart-children::after { 
              content: ''; 
              position: absolute; 
              top: -15px; 
              left: 0; 
              right: 0; 
              height: 2px; 
              background: #ddd; 
              z-index: 1; 
            }
            .org-box-content { 
              padding: 12px; 
              display: flex; 
              align-items: center; 
              gap: 10px; 
            }
            .org-box-icon { 
              font-size: 20px; 
              display: flex; 
              align-items: center; 
              justify-content: center; 
              width: 35px; 
              height: 35px; 
              border-radius: 50%; 
              background: rgba(255, 255, 255, 0.2); 
            }
            .org-box-info { 
              flex: 1; 
              min-width: 0; 
            }
            .org-box-title { 
              font-size: 14px; 
              font-weight: bold; 
              margin: 0 0 3px 0; 
              white-space: nowrap; 
              overflow: hidden; 
              text-overflow: ellipsis; 
            }
            .org-box-subtitle { 
              font-size: 11px; 
              opacity: 0.9; 
              margin: 0 0 2px 0; 
              white-space: nowrap; 
              overflow: hidden; 
              text-overflow: ellipsis; 
            }
            .org-box-email { 
              font-size: 10px; 
              opacity: 0.8; 
              margin: 0; 
              white-space: nowrap; 
              overflow: hidden; 
              text-overflow: ellipsis; 
            }
            .org-box-role { 
              font-size: 9px; 
              opacity: 0.7; 
              margin: 0; 
              font-style: italic; 
            }
            .org-box-count { 
              font-size: 9px; 
              opacity: 0.7; 
              margin: 0; 
            }
            .org-box-desc { 
              font-style: italic; 
              font-size: 10px; 
            }
            @media print {
              body { 
                -webkit-print-color-adjust: exact; 
                print-color-adjust: exact;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            <h1>ORGANIZATIONAL CHART</h1>
            <p>Company Structure & Reporting Hierarchy</p>
            <p class="date">Generated on ${new Date().toLocaleDateString()}</p>
          </div>
          <div class="tree-view">
            ${chartContent}
          </div>
          <script>
            // Auto-trigger print dialog when page loads
            window.onload = function() {
              setTimeout(function() {
                window.print();
              }, 1000);
            };
          </script>
        </body>
        </html>
      `;
      
      // Write content to print window
      printWindow.document.write(printContent);
      printWindow.document.close();
      
    } catch (error) {
      console.error('Error exporting PDF:', error);
      setError('Failed to export PDF');
    } finally {
      setIsExporting(false);
    }
  };

  const renderTreeNode = (node: TreeNode, depth: number = 0): JSX.Element => {
    const hasChildren = node.children.length > 0;
    const isExpanded = node.isExpanded;
    const isDragOver = dragOverNode === node.id;
    const isDragging = draggedUser !== null;
    
    return (
      <div key={node.id} className={`org-chart-node ${node.type}`}>
        <div 
          className={`org-box ${node.type} ${isExpanded ? 'expanded' : ''} ${isDragOver ? 'drag-over' : ''} ${isDragging ? 'dragging' : ''}`}
          onClick={() => {
            if (hasChildren) {
              toggleNode(node.id);
            }
            if (node.type === 'vp' && node.function) {
              // Find the corresponding FunctionWithUsers object
              const funcWithUsers = functions.find(f => f.id === node.function?.id);
              if (funcWithUsers) {
                setSelectedFunction(funcWithUsers);
              }
            }
          }}
          onDragOver={(e) => handleDragOver(e, node.id)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, node.id)}
        >
          <div className="org-box-content">
            <div className="org-box-icon">
              {getRoleIcon(node.type, node.role)}
            </div>
            <div className="org-box-info">
              <div className="org-box-title">{node.name}</div>
              {node.type === 'director' && node.user && (
                <div className="org-box-subtitle">
                  <div className="org-box-email">{node.user.email || 'No email'}</div>
                  <div className="org-box-role">{node.user.role || 'No role'}</div>
                </div>
              )}
              {node.type === 'vp' && node.function && (
                <div className="org-box-subtitle">
                  <div className="org-box-count">{node.function.userCount} Directors</div>
                  {node.function.description && (
                    <div className="org-box-desc">{node.function.description}</div>
                  )}
                </div>
              )}
            </div>
            {node.user && (
              <div 
                className="drag-handle"
                draggable
                onDragStart={(e) => handleDragStart(e, node.user!)}
                onDragEnd={handleDragEnd}
                title="Drag to move user"
              >
                <FaGripVertical />
              </div>
            )}
          </div>
        </div>
        
        {hasChildren && isExpanded && (
          <div className="org-chart-children">
            {node.children.map(child => renderTreeNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const renderUserList = (users: User[]) => {
    if (users.length === 0) {
      return <p className="text-muted">No users assigned to this function</p>;
    }

    return (
      <div className="user-list">
        {users.map((user) => (
          <div key={user.id} className="user-item">
            <div className="user-avatar">
              {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
            </div>
            <div className="user-info">
              <div className="user-name">{user.name || 'Unknown User'}</div>
              <div className="user-email">{user.email || 'No email'}</div>
              <div className="user-role">{user.role || 'No role'}</div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="text-center">
          <Spinner size="lg" color="primary" />
          <p className="mt-3">Loading organization data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid">
        <Alert color="danger">
          <h4>Error Loading Organization Chart</h4>
          <p>{error}</p>
          <button 
            className="btn btn-primary btn-sm" 
            onClick={fetchOrganizationData}
          >
            <FaRedo className="me-2" />
            Retry
          </button>
        </Alert>
      </div>
    );
  }

  const totalUsers = functions.reduce((sum, func) => sum + func.userCount, 0);

  return (
    <div className="container-fluid organization-chart-page">
      <div className="control-buttons mt-5">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h6 className="page-title">
              <FaUsers className="me-2" />
              Organization Chart
            </h6>
                </div>
          <div className="d-flex gap-2">
          <button
            className={`btn ${viewMode === 'tree' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setViewMode('tree')}
          >
            <FaUsers className="me-1" />
            Tree View
          </button>
          <button
            className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setViewMode('list')}
          >
            <FaUser className="me-1" />
            List View
          </button>
          <button
            className="btn btn-outline-secondary"
            onClick={() => setExpandedView(!expandedView)}
          >
            {expandedView ? <FaCompress /> : <FaExpand />}
          </button>
          <button
            className="btn btn-outline-primary"
            onClick={fetchOrganizationData}
          >
            <FaRedo />
          </button>
          <button
            className="btn btn-success"
            onClick={exportToPDF}
            disabled={isExporting || loading}
          >
            {isExporting ? (
              <>
                <Spinner size="sm" className="me-1" />
                Exporting...
              </>
            ) : (
              <>
                <FaFilePdf className="me-1" />
                Export PDF
              </>
            )}
          </button>
          </div>
        </div>
      </div>

      <Row>
        <Col lg={expandedView ? 12 : 8}>
          <Card className="tree-card">
            <CardHeader>
              <h5 className="mb-0">
                {viewMode === 'tree' ? 'Organization Tree' : 'Functions Overview'}
                <span className="badge bg-primary ms-2">Total: {totalUsers} users</span>
              </h5>
            </CardHeader>
            <CardBody>
              <div className={`tree-container ${expandedView ? 'expanded' : ''}`}>
                {viewMode === 'tree' ? (
                  <div ref={chartRef} className="tree-view">
                    {treeData.map(node => renderTreeNode(node))}
                  </div>
                ) : (
                  <div className="functions-list">
                    {functions.map((func, index) => (
                      <div 
                        key={func.id}
                        className={`function-item ${selectedFunction?.id === func.id ? 'selected' : ''}`}
                        onClick={() => setSelectedFunction(func)}
                      >
                        <div className="function-color" style={{ backgroundColor: COLORS[index % COLORS.length] }}></div>
                        <div className="function-info">
                          <div className="function-name">{func.name}</div>
                          <div className="function-count">{func.userCount} users</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        </Col>

        {!expandedView && (
          <Col lg={4}>
            <Card className="functions-list-card">
              <CardHeader>
                <h5 className="mb-0">Functions Overview</h5>
              </CardHeader>
              <CardBody>
                <div className="functions-list">
                  {functions.map((func, index) => (
                    <div 
                      key={func.id}
                      className={`function-item ${selectedFunction?.id === func.id ? 'selected' : ''}`}
                      onClick={() => setSelectedFunction(func)}
                    >
                      <div className="function-color" style={{ backgroundColor: COLORS[index % COLORS.length] }}></div>
                      <div className="function-info">
                        <div className="function-name">{func.name}</div>
                        <div className="function-count">{func.userCount} users</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          </Col>
        )}
      </Row>

      {selectedFunction && (
        <Row className="mt-4">
          <Col>
            <Card>
              <CardHeader>
                <h5 className="mb-0">
                  Users in "{selectedFunction.name}"
                  <span className="badge bg-secondary ms-2">{selectedFunction.userCount} users</span>
                </h5>
              </CardHeader>
              <CardBody>
                {renderUserList(selectedFunction.users)}
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default OrganizationChart;
